import { CanceledError } from 'axios'
import { parseUrl } from 'query-string'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getRetryPredicate } from 'components/status-checks/common/observability-tool-authentication-utils'
import {
  combineMutationResponseStatusFlags,
  USE_COMBINE_MUTATION_RESULT_FLAGS
} from 'components/status-checks/hooks/use-validate-configuration-utils'
import { type StatusCheckFormEvaluationConfiguration } from 'components/status-checks/status-checks-form-utils'
import useAuth from 'hooks/use-auth'
import { useCreateWebhookInvocation } from 'queries/external-integrations/use-create-webook-invocation'
import { useIntegrationInvocationRunTestEvaluation } from 'queries/external-integrations/use-integration-invocations-run-test-evaluation'
import { useStatusCheckRunTestEvaluation } from 'queries/status-checks/use-status-check-run-test-evaluation'
import {
  type EndpointConfiguration,
  type EndpointResponse,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import {
  buildTeamExternalEndpointConfigurationRequest,
  getEndpointConfigurationUrl,
  getIntegrationTypeFromObservabilityToolTypeAndDomain,
  getResponseBodyEvaluationByIntegrationType,
  teamExternalIntegrationsObject
} from 'utils/team-external-integrations/team-external-integrations-util'

export function useValidateEvaluationConfiguration(
  abortController?: AbortController
) {
  const { currentTeamId: teamId } = useAuth()
  const [isCanceled, setIsCanceled] = useState(false)
  const [isPNI, setIsPNI] = useState(false)

  /*
   * Hooks used to execute the evaluation test for status check validation (PNI)
   * NOTE: These execute sequentially, as the evaluation test requires a webhook invocation ID
   */
  const {
    mutateAsync: createWebhookInvocation,
    reset: resetCreateWebhookInvocation,
    ...createWebhookInvocationResponse
  } = useCreateWebhookInvocation(abortController?.signal)

  const {
    mutateAsync: integrationInvocationRunTestEvaluation,
    reset: resetIntegrationInvocationRunTestEvaluation,
    ...integrationInvocationRunTestEvaluationResponse
  } = useIntegrationInvocationRunTestEvaluation(abortController?.signal, {
    retry: getRetryPredicate
  })

  // Hook used to execute the evaluation test for status check validation (public integrations)
  const {
    mutateAsync: runTestEvaluation,
    reset: resetRunTestEvaluation,
    ...runTestEvaluationResponse
  } = useStatusCheckRunTestEvaluation()

  /*
   * Combine the flag states for all mutation results. Flag states here should use OR logic.
   * as the truthiness of any single mutation result should accurately reflect the overall state.
   */
  const { isError, isIdle, isLoading, isSuccess } =
    combineMutationResponseStatusFlags(
      USE_COMBINE_MUTATION_RESULT_FLAGS,
      isPNI
        ? [
            createWebhookInvocationResponse,
            integrationInvocationRunTestEvaluationResponse
          ]
        : [runTestEvaluationResponse]
    )

  // Ensure we don't have any lingering run-test-endpoint requests if hook is unmounted
  useEffect(() => {
    return () => {
      abortController?.abort()
    }
  }, [abortController])

  // Additional mutations may not behave as expected if prior mutations are not reset
  const resetAllMutations = useCallback(() => {
    // Reset mutation states
    resetCreateWebhookInvocation()
    resetIntegrationInvocationRunTestEvaluation()
    resetRunTestEvaluation()

    // Reset local state flags
    setIsCanceled(false)
    setIsPNI(false)
  }, [
    resetCreateWebhookInvocation,
    resetIntegrationInvocationRunTestEvaluation,
    resetRunTestEvaluation
  ])

  const validateEvaluationConfiguration = useCallback(
    async ({
      endpointConfiguration,
      endpointResponse,
      evaluationConfiguration,
      integration
    }: {
      endpointConfiguration?: EndpointConfiguration
      endpointResponse: EndpointResponse
      evaluationConfiguration: StatusCheckFormEvaluationConfiguration
      integration: TeamExternalIntegration
    }) => {
      setIsCanceled(false)
      setIsPNI(false)

      const { domain, isPrivateNetworkIntegration, observabilityToolType } =
        integration

      const evaluationRequestBody = {
        evaluationConfiguration: {
          ...(evaluationConfiguration.requestTimeout !== null && {
            okLatencyMaxMs: evaluationConfiguration.requestTimeout
          }),
          okStatusCodes: [evaluationConfiguration.healthyStatusCode],
          responseBodyEvaluation:
            await getResponseBodyEvaluationByIntegrationType(
              integration,
              evaluationConfiguration,
              teamId
            )
        }
      }

      try {
        if (isPrivateNetworkIntegration) {
          setIsPNI(true)

          // We need to derive a bunch of values to build the endpoint configuration request for the webhook invocation, which is required to run the evaluation test for PNI integrations. This includes the endpoint URL, which may be derived from the integration's observability tool type and domain.
          const integrationType =
            observabilityToolType &&
            getIntegrationTypeFromObservabilityToolTypeAndDomain(
              observabilityToolType,
              domain
            )

          const integrationUrl =
            (await getEndpointConfigurationUrl(integration, teamId)) ||
            (integrationType
              ? teamExternalIntegrationsObject?.[integrationType]?.authUrl
              : undefined)

          if (!parseUrl(integrationUrl)) {
            throw new Error(
              `integration url (${integrationUrl}) is not a valid url`
            )
          }

          /*
           * A webhook invocation ID is required to run the evaluation test in PNI cases. The webhookID
           * is a required segment to construct the proper endpoint URL.
           */
          const { invocationId } = await createWebhookInvocation(
            buildTeamExternalEndpointConfigurationRequest(
              integration,
              integrationUrl,
              undefined,
              undefined,
              endpointConfiguration
            )
          )

          const invocationTestResponse =
            await integrationInvocationRunTestEvaluation([
              invocationId,
              evaluationRequestBody
            ])

          return Promise.resolve(invocationTestResponse)
        } else {
          const testResponse = await runTestEvaluation({
            ...evaluationRequestBody,
            testEndpointResponse: {
              ...endpointResponse,
              contentType: endpointResponse.contentType || 'text/plain'
            }
          })

          return Promise.resolve(testResponse)
        }
      } catch (error) {
        if (error instanceof CanceledError) {
          setIsCanceled(true)
        }

        return Promise.reject(error)
      }
    },
    [
      teamId,
      createWebhookInvocation,
      integrationInvocationRunTestEvaluation,
      runTestEvaluation
    ]
  )

  return useMemo(
    () => ({
      isCanceled,
      isError,
      isIdle,
      isLoading,
      isSuccess,
      reset: resetAllMutations,
      validateEvaluationConfiguration
    }),
    [
      isCanceled,
      isError,
      isIdle,
      isLoading,
      isSuccess,
      resetAllMutations,
      validateEvaluationConfiguration
    ]
  )
}
